<template>
  <div>
    <!-- Shown only when there is something to say: an empty card would push the timeline down for
         nothing. -->
    <NeedsAttentionCardComponent
      v-if="
        mandates.attentionRows.length > 0 || mandates.attentionError !== null
      "
      class="mb-3"
      :rows="mandates.attentionRows"
      :error="mandates.attentionError"
      :description="$t('forwards.mandates.attention.description')"
      @retry="mandates.loadAttention"
    />

    <div
      v-if="mandates.hasNoMandates"
      class="mandates-intro border rounded-3 p-4 d-flex align-items-start gap-3"
      data-testid="mandates-intro"
    >
      <FontAwesomeIcon
        icon="fa-solid fa-calendar-alt"
        class="text-primary fs-3 flex-shrink-0"
      />
      <div>
        <h5 class="mb-2">{{ $t("forwards.mandates.intro.title") }}</h5>
        <ForwardSalesIntroComponent :country class="mb-3" />
        <button
          type="button"
          class="btn btn-primary"
          data-testid="create-first-mandate"
          @click="mandateEditor.open(null)"
        >
          <FontAwesomeIcon icon="fa-solid fa-plus" class="me-1" />
          {{ $t("forwards.mandates.intro.cta") }}
        </button>
      </div>
    </div>

    <template v-else>
      <div class="d-flex align-items-end flex-wrap gap-3 mb-3">
        <TimelineYearStepperComponent v-model="mandates.year" />

        <div class="mandates-filter">
          <label
            class="eyebrow-label eyebrow-label--sm mb-1"
            :for="statusFilterId"
            >{{ $t("forwards.mandates.status_filter") }}</label
          >
          <MultiSelectComponent
            v-model="mandates.statuses"
            :options="statusOptions"
            :buttonId="statusFilterId"
            :placeholder="$t('forwards.mandates.status_filter_all')"
            size="sm"
            hideBulkActions
          />
        </div>

        <div class="mandates-filter">
          <label
            class="eyebrow-label eyebrow-label--sm mb-1"
            :for="nameFilterId"
            >{{ $t("forwards.mandates.name_filter") }}</label
          >
          <input
            :id="nameFilterId"
            v-model="mandates.nameFilter"
            type="search"
            class="form-control form-control-sm"
            :placeholder="$t('forwards.mandates.name_filter_placeholder')"
          />
        </div>

        <div class="mandates-filter">
          <label
            class="eyebrow-label eyebrow-label--sm mb-1"
            :for="companyFilterId"
            >{{ $t("forwards.mandates.company_filter") }}</label
          >
          <MultiSelectComponent
            v-model="mandates.companyIds"
            :options="companyOptions"
            :buttonId="companyFilterId"
            :placeholder="$t('forwards.mandates.company_filter_all')"
            size="sm"
          />
        </div>

        <ul
          class="mandates-legend list-unstyled d-flex align-items-center gap-3 mb-0 ms-auto"
        >
          <li class="d-flex align-items-center gap-1">
            <span class="forwards-swatch forwards-swatch--escrowed"></span
            >{{ $t("forwards.common.escrowed") }}
          </li>
          <li class="d-flex align-items-center gap-1">
            <span class="forwards-swatch forwards-swatch--committed"></span
            >{{ $t("forwards.common.committed") }}
          </li>
          <li class="d-flex align-items-center gap-1">
            <span class="forwards-swatch forwards-swatch--available"></span>
            <InfoTooltip :text="$t('forwards.mandates.available_help')">{{
              $t("forwards.common.available")
            }}</InfoTooltip>
          </li>
        </ul>
      </div>

      <div v-if="errorMessage" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ errorMessage }}</span>
        <button
          type="button"
          class="btn btn-sm btn-link ms-auto p-0"
          @click="mandates.load()"
        >
          {{ $t("forwards.common.retry") }}
        </button>
      </div>

      <div class="border rounded-3 p-3">
        <LoadingIndicatorComponent
          v-if="mandates.loading"
          :message="$t('forwards.mandates.loading')"
        />
        <p
          v-else-if="mandates.isEmpty"
          class="text-body-tertiary text-center py-4 mb-0"
        >
          {{ $t("forwards.mandates.empty") }}
        </p>
        <template v-else-if="mandates.timeline">
          <!-- The band stays the whole scope's total: it is titled "All mandates", and the name filter
               only narrows which rows are on screen. -->
          <MandateTimelineBandComponent
            v-if="mandates.rows.length > 0"
            :band="mandates.band"
            :windowQuarters="mandates.windowQuarters"
            :bandNote="mandates.timeline.band_note"
          />
          <p
            v-if="mandates.noNameMatches"
            class="text-body-tertiary text-center py-4 mb-0"
          >
            {{ $t("forwards.mandates.name_filter_no_matches") }}
          </p>
          <MandateTimelineRowComponent
            v-for="row in mandates.filteredRows"
            :key="row.mandate_id"
            :row="row"
            :windowQuarters="mandates.windowQuarters"
          />
          <div
            v-if="mandates.filteredRestrictedMandates.length > 0"
            class="restricted-mandates mt-3"
          >
            <div
              v-for="mandate in mandates.filteredRestrictedMandates"
              :key="mandate.mandate_id"
              class="restricted-mandate d-flex align-items-start gap-2"
            >
              <FontAwesomeIcon
                icon="fa-solid fa-lock"
                class="text-body-tertiary mt-1"
              />
              <div>
                <div class="fw-semibold">{{ mandate.name }}</div>
                <div class="small text-body-tertiary">
                  {{ $t("forwards.mandates.restricted_access") }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <!-- Not while loading: an account without mandates would see it flash before the introduction,
           which holds the same materials. -->
      <ForwardSalesGuideComponent
        v-if="!mandates.loading"
        :country
        class="mt-3"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import ForwardSalesGuideComponent from "@/forwards2/components/ForwardSalesGuideComponent.vue";
import ForwardSalesIntroComponent from "@/forwards2/components/ForwardSalesIntroComponent.vue";
import NeedsAttentionCardComponent from "@/forwards2/overview/NeedsAttentionCardComponent.vue";
import { forwardsErrorMessage } from "@/forwards2/forwardsApi";
import { MANDATE_STATUS_FILTERS } from "@/forwards2/forwardsTypes";
import TimelineYearStepperComponent from "@/forwards2/mandates/TimelineYearStepperComponent.vue";
import MandateTimelineBandComponent from "@/forwards2/mandates/MandateTimelineBandComponent.vue";
import MandateTimelineRowComponent from "@/forwards2/mandates/MandateTimelineRowComponent.vue";
import { useMandateEditorLauncher } from "@/forwards2/mandates/useMandateEditorLauncher";
import { useMandatesTimeline } from "@/forwards2/mandates/useMandatesTimeline";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The mandates in the dashboard's scope over one calendar year: their Needs attention rows, the timeline
 * with its filters, and the collapsible forward sales guide below it. An account that has never had a
 * mandate gets an introduction to forward sales, with the forward sales materials open and a button to
 * create its first mandate, instead of the filters, an empty timeline and the guide; filters that merely hide every mandate still show the timeline's empty state. A mandate's name opens the editor modal the forward
 * card renders; the card calls `reload` after an editor session that wrote.
 *
 * `companyIds` seeds the company filter once, at setup: the open company in a single-company view,
 * none in the consolidated one, which shows every mandate in the account's scope. The user may clear or
 * change it from there. Mount the panel per seed — a dashboard switching companies has to remount
 * it, or it keeps the previous company's filter.
 */
const props = withDefaults(
  defineProps<{
    companyIds: number[];
    /** Picks the materials' language versions (`ForwardSalesMaterialsComponent`). */
    country?: string | null;
  }>(),
  { country: null }
);

const { t } = useI18n();
const mandates = useMandatesTimeline(props.companyIds);
const mandateEditor = useMandateEditorLauncher();

const statusFilterId = useId();
const companyFilterId = useId();
const nameFilterId = useId();

const statusOptions = computed(() =>
  MANDATE_STATUS_FILTERS.map((value) => ({
    value,
    label: t(`forwards.mandates.status.${value}`),
  }))
);

const companyOptions = computed(() =>
  mandates.companies.map((company) => ({
    value: company.company_id,
    label: company.name,
  }))
);

const errorMessage = computed(() =>
  mandates.error === null
    ? null
    : (forwardsErrorMessage(mandates.error) ??
      t("forwards.mandates.load_failed"))
);

defineExpose({ reload: mandates.reload });
</script>

<style scoped lang="scss">
@use "@/forwards2/forwards-colors" as colors;

.mandates-filter {
  min-width: 13rem;
}

.mandates-legend {
  font-size: 0.75rem;
  color: var(--phoenix-gray-600);
}

.restricted-mandates {
  border-top: 1px solid var(--phoenix-border-color-translucent);
}

.restricted-mandate {
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--phoenix-border-color-translucent);
}

.forwards-swatch {
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 2px;
  flex-shrink: 0;
}

.forwards-swatch--escrowed {
  background: colors.$forwards-escrowed;
}

.forwards-swatch--committed {
  background: colors.$forwards-committed;
}

.forwards-swatch--available {
  background: colors.$forwards-available;
}
</style>
